/**
 * Photos (owner: a camera in the pockets takes a picture or a selfie; the traffic cameras' letters carry the picture of you).
 * Everything here is client-side: the frame is copied off the game canvas (world only, before the HUD), shrunk to a JPEG
 * and kept in this browser (localStorage, the newest PHOTO_MAX). The start card lists them with a download link.
 */
import { photos as snapshots } from './snapshots';
import { drawEvidence, type EvidenceOpts } from '../draw/evidence';
import type { PhotoJob } from './photo.worker';

export type Photo = { id: string; t: number; kind: 'photo' | 'selfie' | 'evidence' | 'drone'; caption: string; data: string };
const KEY = 'sprawl.photos', PHOTO_MAX = 16, WIDTH = 720;

export function loadPhotos(): Photo[] {
  snapshots(); // (Sep 25) the Home app's snapshots once shared this key: they move to their own before this list is read or rewritten
  try { const list = JSON.parse(localStorage.getItem(KEY) ?? '[]') as Photo[]; return Array.isArray(list) ? list.filter((p) => !!p && typeof p.id === 'string' && typeof p.data === 'string') : []; } catch { return []; }
}
function savePhotos(list: Photo[]): void { try { localStorage.setItem(KEY, JSON.stringify(list.slice(-PHOTO_MAX))); } catch { /* full or blocked: the picture is lost, the game is not */ } }
/** (Sep 29) the Photos app's Delete */
export function removePhotoById(id: string): void { savePhotos(loadPhotos().filter((p) => p.id !== id)); renderGallery(); }
export function photoById(id: string): Photo | undefined { return loadPhotos().find((p) => p.id === id); }
/** a traffic fine's picture: the evidence this browser copied at the flash, kept under the fine id the letter comes with (cameras.ts) */
export function evidenceFor(letterId: string): Photo | undefined { const ph = photoById(letterId); return ph?.kind === 'evidence' ? ph : undefined; }

/**
 * (owner, Sep 25: "when driving fast … a noticeable jerking") the JPEGs are made off the main thread (photo.worker.ts): `toDataURL`
 * and `toBlob` both encoded on it, and a speed camera's evidence picture was 200+ ms at every flash, mid-drive
 * (tools/audit/drive-pacing.mjs). Where there is no worker (no OffscreenCanvas) the old way, on this thread.
 */
let photoWorker: Worker | null | undefined;
const photoWaiting = new Map<number, (url: string) => void>();
let photoJobId = 0;
function worker(): Worker | null {
  if (photoWorker !== undefined) return photoWorker;
  try {
    photoWorker = typeof OffscreenCanvas !== 'undefined' && typeof Worker !== 'undefined' ? new Worker(new URL('./photo.worker.ts', import.meta.url), { type: 'module' }) : null;
    if (photoWorker) photoWorker.onmessage = (e: MessageEvent<{ id: number; url: string }>) => { const f = photoWaiting.get(e.data.id); photoWaiting.delete(e.data.id); f?.(e.data.url); };
  } catch { photoWorker = null; }
  return photoWorker;
}
function keep(photo: Photo, data: string, done?: (ph: Photo) => void): void { if (!data) return; photo.data = data; const list = loadPhotos().filter((p) => p.id !== photo.id); list.push(photo); savePhotos(list); done?.(photo); }
function encodeJpeg(cv: HTMLCanvasElement, ctx: CanvasRenderingContext2D, q: number, done: (url: string) => void): void {
  const w = worker();
  if (!w) { done(cv.toDataURL('image/jpeg', q)); return; }
  const id = ++photoJobId, img = ctx.getImageData(0, 0, cv.width, cv.height);
  photoWaiting.set(id, (url) => done(url || cv.toDataURL('image/jpeg', q))); // (the worker failed: the slow way, once)
  w.postMessage({ id, q, kind: 'pixels', w: cv.width, h: cv.height, buf: img.data.buffer } satisfies PhotoJob, [img.data.buffer]);
}
/** A speed camera's evidence picture (draw/evidence.ts), drawn and kept in the worker's time; `done` when it is in Photos. */
export function evidencePhoto(shot: EvidenceOpts, caption: string, id: string, done?: (ph: Photo) => void): void {
  const photo: Photo = { id, t: Date.now(), kind: 'evidence', caption, data: '' }, W = 720, H = 480, q = 0.72;
  const here = (): void => { try { const cv = document.createElement('canvas'); cv.width = W; cv.height = H; drawEvidence(cv.getContext('2d')!, W, H, shot); keep(photo, cv.toDataURL('image/jpeg', q), done); } catch { /* no picture: the letter goes without one */ } };
  const w = worker();
  if (!w) { here(); return; }
  const jid = ++photoJobId;
  photoWaiting.set(jid, (url) => { if (url) keep(photo, url, done); else here(); });
  w.postMessage({ id: jid, q, kind: 'evidence', w: W, h: H, shot } satisfies PhotoJob);
}

/** Copy the canvas (the world as drawn this frame) into a small JPEG and keep it. The copy is taken now; the JPEG and the keeping
 *  follow a moment later (`done` then — the encoding is off the main thread, `encodeJpeg`). */
export function takePhoto(canvas: HTMLCanvasElement, kind: Photo['kind'], caption: string, id = `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`, done?: (ph: Photo) => void): Photo | undefined {
  try {
    const sc = Math.min(1, WIDTH / canvas.width);
    const cv = document.createElement('canvas'); cv.width = Math.round(canvas.width * sc); cv.height = Math.round(canvas.height * sc);
    const ctx = cv.getContext('2d', { willReadFrequently: true })!; /* (a canvas in memory, not on the GPU: its pixels are read without a read-back — Sep 25) */ ctx.drawImage(canvas, 0, 0, cv.width, cv.height);
    // a date stamp in the corner, like a cheap camera
    ctx.font = `bold ${Math.max(11, cv.height * 0.028)}px monospace`; ctx.fillStyle = 'rgba(255,170,60,0.9)'; ctx.textAlign = 'right';
    ctx.fillText(caption, cv.width - 10, cv.height - 10);
    const photo: Photo = { id, t: Date.now(), kind, caption, data: '' };
    encodeJpeg(cv, ctx, 0.72, (data) => keep(photo, data, done));
    return photo;
  } catch { return undefined; }
}

/** The start card's gallery: thumbnails, newest first, each a download link. */
export function renderGallery(): void {
  const det = document.getElementById('photos') as HTMLDetailsElement | null, body = document.getElementById('photosbody');
  if (!det || !body) return;
  const list = loadPhotos().slice().reverse();
  det.hidden = list.length === 0;
  body.innerHTML = '';
  for (const ph of list) {
    const a = document.createElement('a'); a.href = ph.data; a.download = `sprawl-${ph.kind}-${new Date(ph.t).toISOString().slice(0, 16).replace(/[:T]/g, '-')}.jpg`; a.title = `${ph.caption} — click to save`;
    a.style.cssText = 'display:block;width:110px;border:2px solid #3a4a5c;border-radius:6px;overflow:hidden;background:#000';
    const img = document.createElement('img'); img.src = ph.data; img.alt = ph.caption; img.style.cssText = 'display:block;width:100%;height:auto';
    const cap = document.createElement('div'); cap.textContent = ph.kind === 'evidence' ? '📸 evidence' : ph.kind === 'selfie' ? '🤳 selfie' : ph.kind === 'drone' ? ph.caption.split(' · ')[0] : '📷 photo'; // (Sep 25) a drone's: 🛸 the place, from how high cap.style.cssText = 'font-size:11px;color:#c8d4e0;padding:2px 4px;text-align:center';
    a.append(img, cap); body.append(a);
  }
  const sum = document.getElementById('photossum'); if (sum) sum.textContent = `Photos (${list.length})`;
}

/** The letter overlay (a fine from a traffic camera, with its picture when this browser took one). */
export function showLetter(title: string, text: string, photoId?: string, /** (C) a picture from the server (the Media page's pick) — shown, and kept in Photos */ url?: string): void {
  const box = document.getElementById('letter') as HTMLDivElement | null; if (!box) return;
  (document.getElementById('lettertitle') as HTMLDivElement).textContent = title;
  (document.getElementById('lettertext') as HTMLDivElement).textContent = text;
  const img = document.getElementById('letterphoto') as HTMLImageElement;
  const ph = photoId ? photoById(photoId) : undefined;
  img.hidden = !ph && !url; if (ph) img.src = ph.data; else if (url) { img.src = url; void keepPicture(url, title); }
  box.hidden = false;
  const ok = document.getElementById('letterok') as HTMLButtonElement; ok.onclick = () => { box.hidden = true; };
}

/** (C) a picture that came with a letter is kept in Photos like one the player took (once per file) */
export async function keepPicture(url: string, caption: string): Promise<void> {
  const id = `m-${url.split('/').pop()?.replace(/[^a-z0-9_]/gi, '') ?? ''}`;
  if (!id || photoById(id)) return;
  try {
    const blob = await (await fetch(url)).blob();
    if (!blob.type.startsWith('image/')) return;
    const data = await new Promise<string>((res, rej) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result)); fr.onerror = () => rej(fr.error); fr.readAsDataURL(blob); });
    const list = loadPhotos().filter((p) => p.id !== id); list.push({ id, t: Date.now(), kind: 'photo', caption: caption.slice(0, 60), data }); savePhotos(list);
  } catch { /* the letter still showed it */ }
}
